import { useCallback, useState } from "react";
import { useMutation } from "react-query";
import { Box, Button, Flex, Text, TextField } from "@radix-ui/themes";
import { toast } from "sonner";

import { useRegressionApi } from "@/v2/entities/regression/Regression.api";
import { RegressionDecision } from "@/v2/entities/regression/Regression.model";
import { Dialog } from "@/components/shared/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import styles from "./styles.module.scss";

interface AddItemDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  regressionSetId: string;
  onSuccess: () => void;
}

export function AddItemDialog({
  open,
  onOpenChange,
  regressionSetId,
  onSuccess,
}: AddItemDialogProps) {
  const regressionApi = useRegressionApi();
  const [question, setQuestion] = useState("");
  const [expectedAnswer, setExpectedAnswer] = useState("");
  const [expectedDecision, setExpectedDecision] =
    useState<RegressionDecision>("none");

  const isAnswerRequired = expectedDecision === "none";

  const { mutate: addItem, isLoading } = useMutation(
    () =>
      regressionApi.createRegressionSetItem(regressionSetId, {
        question,
        ...(isAnswerRequired ? { expectedAnswer } : { expectedDecision }),
      }),
    {
      onSuccess: () => {
        toast.success("Item added successfully");
        setQuestion("");
        setExpectedAnswer("");
        setExpectedDecision("none");
        onSuccess();
      },
      onError: () => {
        toast.error("Failed to add item");
      },
    }
  );

  const handleClose = useCallback(() => {
    setQuestion("");
    setExpectedAnswer("");
    setExpectedDecision("none");
    onOpenChange(false);
  }, [onOpenChange]);

  const handleCreate = useCallback(() => {
    if (!question.trim()) {
      toast.error("Please enter a question");
      return;
    }
    if (isAnswerRequired && !expectedAnswer.trim()) {
      toast.error("Please enter an expected answer");
      return;
    }
    addItem();
  }, [question, expectedAnswer, isAnswerRequired, addItem]);

  const handleExpectedDecisionChange = useCallback((value: string) => {
    setExpectedDecision(value as RegressionDecision);
    if (value !== "none") {
      setExpectedAnswer("");
    }
  }, []);

  const handleQuestionChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setQuestion(e.target.value);
    },
    []
  );

  const handleExpectedAnswerChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      setExpectedAnswer(e.target.value);
    },
    []
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <Dialog.Content size="medium">
        <Flex direction="column" gap="4" p="4">
          <Text size="5" weight="medium">
            Add Item
          </Text>

          <Text size="2" color="gray">
            Add a question and expected answer to this test set.
          </Text>

          <Box>
            <Text size="2" weight="regular" mb="1" as="label">
              Question
            </Text>
            <TextField.Root
              placeholder="Enter the question"
              value={question}
              onChange={handleQuestionChange}
            />
          </Box>

          <Box>
            <Text size="2" weight="regular" mb="1" as="label">
              Expected Answer
            </Text>
            <textarea
              className={styles.textArea}
              placeholder={
                isAnswerRequired
                  ? "Enter the expected answer"
                  : "Not applicable for this decision"
              }
              value={expectedAnswer}
              onChange={handleExpectedAnswerChange}
              rows={4}
              disabled={!isAnswerRequired}
            />
          </Box>

          <Box>
            <Text size="2" weight="regular" mb="1" as="label">
              Expected Decision
            </Text>
            <Select
              value={expectedDecision}
              onValueChange={handleExpectedDecisionChange}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">None</SelectItem>
                <SelectItem value="escalate">Escalate</SelectItem>
              </SelectContent>
            </Select>
          </Box>

          <Flex gap="3" mt="2" justify="end">
            <Button variant="soft" color="gray" onClick={handleClose}>
              Cancel
            </Button>
            <Button
              color="gray"
              highContrast
              onClick={handleCreate}
              disabled={
                isLoading ||
                !question.trim() ||
                (isAnswerRequired && !expectedAnswer.trim())
              }
            >
              {isLoading ? "Adding..." : "Add Item"}
            </Button>
          </Flex>
        </Flex>
      </Dialog.Content>
    </Dialog>
  );
}
